import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { Paginated } from '@next/core-lib/models';
import { Observable } from 'rxjs';
import { RequestsOverviewModel } from 'src/app/dashboard/components/overview/models/overview.model';
import { PageAndSize } from 'src/app/dashboard/components/overview/models/paginated-overview.model';
import { ManureFilters } from 'src/app/shared/models/manure-filters.model';

import { Environment } from 'src/environments/environment';

import {
  ManureRequest,
  ManureRequestModel,
  RequestExportApiModel,
} from '../../request/models/manure-request.model';
import { Status } from '../models/status.model';

@Injectable({
  providedIn: 'root',
})
export class RequestService {
  protected http = inject(HttpClient);
  private readonly env = inject<Environment>(EnvironmentInjector);

  protected baseUrl = this.env.APIM_BASE_URL + '/mestplace-v2-service';

  postRequest(manureRequest: ManureRequest): Observable<ManureRequestModel> {
    return this.http.post<ManureRequestModel>(`${this.baseUrl}/requests`, manureRequest);
  }

  getRequests(
    paginationOptions: PageAndSize,
    filters?: ManureFilters,
  ): Observable<Paginated<ManureRequestModel>> {
    const { pageIndex, pageSize } = paginationOptions;
    const params = {
      pageIndex,
      pageSize,
      ...(filters?.amountFrom ? { amountFrom: filters.amountFrom } : {}),
      ...(filters?.amountTo ? { amountTo: filters.amountTo } : {}),
      ...(filters?.desiredDeliveryPeriodFrom
        ? { desiredDeliveryPeriodFrom: filters.desiredDeliveryPeriodFrom }
        : {}),
      ...(filters?.desiredDeliveryPeriodTo
        ? { desiredDeliveryPeriodTo: filters.desiredDeliveryPeriodTo }
        : {}),
      ...(filters?.distance ? { distance: filters.distance } : {}),
      ...(filters?.isHomogeneous != null ? { isHomogeneous: filters.isHomogeneous } : {}),
      ...(filters?.manureCategory ? { manureCategory: filters.manureCategory } : {}),
      ...(filters?.manureType ? { manureType: filters.manureType } : {}),
      ...{ status: [Status.new, Status.matchRequested] },
    };

    return this.http.get<Paginated<ManureRequestModel>>(`${this.baseUrl}/requests`, {
      params,
    });
  }

  getRequestsForExport() {
    return this.http.get<{ requests: RequestExportApiModel[] }>(`${this.baseUrl}/requests/export`);
  }

  getMyActiveRequests(paginationOptions: PageAndSize): Observable<Paginated<ManureRequestModel>> {
    const { pageIndex, pageSize } = paginationOptions;
    const params = {
      pageIndex,
      pageSize,
      myAdvertisementsOnly: true,
      ...{ status: [Status.new, Status.matchRequested] },
    };

    return this.http.get<Paginated<ManureRequestModel>>(`${this.baseUrl}/requests`, {
      params,
    });
  }

  getMyClosedRequests(paginationOptions: PageAndSize): Observable<Paginated<ManureRequestModel>> {
    const { pageIndex, pageSize } = paginationOptions;
    const params = {
      pageIndex,
      pageSize,
      myAdvertisementsOnly: true,
      ...{ status: Status.matchApproved },
    };

    return this.http.get<Paginated<ManureRequestModel>>(`${this.baseUrl}/requests`, {
      params,
    });
  }

  makeMatch(id: number): Observable<RequestsOverviewModel> {
    return this.http.post<RequestsOverviewModel>(`${this.baseUrl}/requests/${id}/match`, id);
  }

  acceptMatch(id: number): Observable<RequestsOverviewModel> {
    return this.http.post<RequestsOverviewModel>(
      `${this.baseUrl}/requests/${id}/match/approve`,
      id,
    );
  }

  rejectMatch(id: number): Observable<RequestsOverviewModel> {
    return this.http.post<RequestsOverviewModel>(`${this.baseUrl}/requests/${id}/match/reject`, id);
  }

  deleteRequest(id: number): Observable<HttpResponse<object>> {
    return this.http.delete(`${this.baseUrl}/requests/${id}`, { observe: 'response' });
  }
}
